import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import Cookies from "js-cookie";
import useUserStore from "../store/user";
import { assertApiEnvelopeSuccess } from "../utils/apiEnvelope";

const uuidPattern =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

const createCorrelationId = () =>
  globalThis.crypto?.randomUUID?.() ||
  `corr-${Date.now()}-${Math.random().toString(16).slice(2)}`;

class AxiosService {
  axiosInstance: AxiosInstance;

  multiPartFormDataConfig: AxiosRequestConfig = {
    baseURL: import.meta.env.VITE_APP_URL_BASE,
    headers: {
      "Content-Type": "application/json",
    },
  };

  constructor() {
    const axiosConfig: AxiosRequestConfig = {
      baseURL: import.meta.env.VITE_APP_URL_BASE,
      headers: {},
      // withCredentials: true,
    };

    this.axiosInstance = axios.create(axiosConfig);

    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        if (!config.url) {
          return config;
        }

        const urlPath = this.getUrlPath(config.url);
        if (config.headers) {
          config.headers["x-correlation-id"] =
            (config.headers["x-correlation-id"] as string | undefined) ||
            createCorrelationId();

          const profilePublicId = config.url.match(uuidPattern)?.[0];
          if (profilePublicId) {
            config.headers["x-profile-public-id"] = profilePublicId;
          }
        }

        const excludedRoutes = ["/auth/", "/imports-manager/oauth/google"];

        const isExcluded = excludedRoutes.some((route) =>
          urlPath.startsWith(route),
        );

        if (!isExcluded) {
          // Adicionar XSRF-TOKEN
          const xsrfToken = Cookies.get("XSRF-TOKEN");
          if (xsrfToken && config.headers) {
            config.headers["X-XSRF-TOKEN"] = xsrfToken;
          }

          // Adicionar Authorization Bearer Token
          const { user } = useUserStore.getState();
          if (user?.token && config.headers) {
            config.headers["Authorization"] = `Bearer ${user.token}`;
          }
        } else {
          console.debug(`Interceptor ignorando a rota: ${urlPath}`);
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    this.axiosInstance.interceptors.response.use(
      (response) => {
        assertApiEnvelopeSuccess(response.data);
        return response;
      },
      (error) => Promise.reject(error),
    );
  }

  private getUrlPath(url: string): string {
    try {
      const fullUrl = new URL(url, import.meta.env.VITE_APP_URL_BASE);
      return fullUrl.pathname;
    } catch (e) {
      return url;
    }
  }

  /**
   * Métodos HTTP
   */

  async get(path: string, config?: AxiosRequestConfig): Promise<any> {
    return this.axiosInstance.get(path, config);
  }

  async post(
    path: string,
    params?: any,
    config?: AxiosRequestConfig,
  ): Promise<any> {
    return this.axiosInstance.post(path, params, config);
  }

  async postT<Body, Result>(
    path: string,
    params?: Body,
    customConfig?: AxiosRequestConfig,
  ): Promise<Result> {
    const response = await this.axiosInstance.post(path, params, customConfig);
    return response.data;
  }

  async put(
    path: string,
    params: any,
    config?: AxiosRequestConfig,
  ): Promise<any> {
    return this.axiosInstance.put(path, params, config);
  }

  async putT<Body, Result>(path: string, params: Body): Promise<Result> {
    const response = await this.axiosInstance.put(path, params);
    return response.data;
  }

  async delete(path: string, params?: any): Promise<any> {
    return this.axiosInstance.delete(path, { data: params });
  }

  async patch(path: string, params: any): Promise<any> {
    return this.axiosInstance.patch(path, params);
  }

  async head(path: string, params?: any): Promise<any> {
    return this.axiosInstance.head(path, { params });
  }

  /**
   * Upload de arquivos
   */
  async uploadFile(
    url: string,
    data: FormData,
    customConfig?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    const customAxiosRequestConfig =
      customConfig || this.multiPartFormDataConfig;
    return this.axiosInstance.post(url, data, customAxiosRequestConfig);
  }
}

const ApiService: AxiosService = new AxiosService();

export { ApiService };
